import { getGeoLocation } from '@/helpers';
import { showGeolocationErrorToast } from '@/helpers/toast';
import { locationSlug } from '@/helpers/urlTs';
import { DEFAULT_MY_LOCATION_KEYWORD } from '@/hooks/search/useSearch.constants';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { SortOption } from '@/types/state/search';
import { useState } from 'react';
import { getSelectedFilters } from '../../components/Filter/Filter.helpers';
import { FilterOption } from '../../components/Filter/Filter.types';

type SearchControlProps = BaseSearchManagerOutput;

export type SearchControlsManagerOutput = ReturnType<typeof useSearchControlsManager>;

const baseTranslationKey = 'features.searchV2.modules.SearchControls';

const useSearchControlsManager = (props: SearchControlProps) => {
  const [isLoadingGeoLocation, setIsLoadingGeoLocation] = useState<boolean>(false);

  function handleClearSorting() {
    /**
     * If user clears sorting by distance (closest), we need to clear the lat and lon
     * in all cases where the user is not doing a "near me" location search
     */
    if (locationSlug(props.location) !== DEFAULT_MY_LOCATION_KEYWORD) {
      props.performSearch({ sort: null, lat: undefined, lon: undefined });
      return;
    }

    props.performSearch({ sort: null });
  }

  function handleOnChangeSorting(sort: SortOption) {
    /**
     *  if we are sorting by closest, we need to clear the location since we are not using it
     *  instead we want to sort by the closest location to the user position
     */
    if (sort === 'closest') {
      getGeoLocation(
        // on success handler
        (lat: string, lon: string) => {
          props.performSearch({ sort, lat: +lat, lon: +lon });
          setIsLoadingGeoLocation(false);
        },
        () => {},
        // on loading handler
        () => {
          setIsLoadingGeoLocation(true);
        },
        // on error handler
        (error: GeolocationPositionError) => {
          setIsLoadingGeoLocation(false);
          const deniedKey = `${baseTranslationKey}.snackbar.error.userLocationDenied` as const;
          showGeolocationErrorToast(error, { deniedKey, autoClose: false });
        },
      );
    } else {
      const keepCoordinates = locationSlug(props.location) === DEFAULT_MY_LOCATION_KEYWORD;
      props.performSearch({ sort, ...(!keepCoordinates && { lat: undefined, lon: undefined }) });
    }
  }

  function handleOnChangeAppliedFilters(prefs: string) {
    props.performSearch({ prefs });
  }

  function handleOnRemoveSingleFilter(filterId: FilterOption['id']) {
    return (event) => {
      event?.stopPropagation();
      event?.preventDefault();
      const selectedOptions = getSelectedFilters(props.prefs);
      const newOptions = selectedOptions.filter((option) => option.id !== filterId).map((option) => option.id);
      props.performSearch({ prefs: newOptions.join(',') });
    };
  }

  function handleClearFilters() {
    props.performSearch({ prefs: undefined });
  }

  return {
    isLoadingGeoLocation,
    onChangeAppliedFilters: handleOnChangeAppliedFilters,
    onChangeSorting: handleOnChangeSorting,
    onRemoveSingleFilter: handleOnRemoveSingleFilter,
    onClearFilters: handleClearFilters,
    onClearSorting: handleClearSorting,
  };
};

export default useSearchControlsManager;
